import { useCallback, useEffect } from 'react';

import useMaskedInput from 'hooks/useMaskedInput';
import { haToAc, unMaskInputValue } from 'utils/helpers';
import { Metric } from 'utils/constants';
import { BoardFertilization } from 'features/Croptimizer/types';
import { TypeOfKey } from 'utils/types';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

export const useFertilizationInputs = () => {
  const {
    fertilizationValues,
    setFertilizationValues,
    metricValue,
  } = useCroptimizer();

  const changeFertilizationValues = useCallback(
    (name: keyof BoardFertilization) => (value: TypeOfKey<BoardFertilization>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setFertilizationValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    },
    [setFertilizationValues],
  );

  const {
    value: inputN,
    setValue: setInputN,
    clean: cleaninputn,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric
        ? '180'
        : unMaskInputValue(String(haToAc(180))),
    value:
      fertilizationValues?.nvalue ??
      (metricValue === Metric.metric
        ? '180'
        : unMaskInputValue(String(haToAc(180)))),
    onChangeCallback: changeFertilizationValues('nvalue'),
  });

  const {
    value: inputP,
    setValue: setInputP,
    clean: cleaninputp,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric
        ? '50'
        : unMaskInputValue(String(haToAc(50))),
    value:
      fertilizationValues?.pvalue ??
      (metricValue === Metric.metric
        ? '50'
        : unMaskInputValue(String(haToAc(50)))),
    onChangeCallback: changeFertilizationValues('pvalue'),
  });

  const {
    value: inputK,
    setValue: setInputK,
    clean: cleaninputk,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric
        ? '70'
        : unMaskInputValue(String(haToAc(70))),
    value:
      fertilizationValues?.kvalue ??
      (metricValue === Metric.metric
        ? '70'
        : unMaskInputValue(String(haToAc(70)))),
    onChangeCallback: changeFertilizationValues('kvalue'),
  });

  const {
    value: pricesN,
    setValue: setPricesN,
    clean: cleanpricesn,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric ? '2' : unMaskInputValue(String(haToAc(2))),
    value:
      fertilizationValues?.nprice ??
      (metricValue === Metric.metric
        ? '2'
        : unMaskInputValue(String(haToAc(2)))),
    onChangeCallback: changeFertilizationValues('nprice'),
  });

  const {
    value: pricesP,
    setValue: setPricesP,
    clean: cleanpricesp,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric ? '2' : unMaskInputValue(String(haToAc(2))),
    value:
      fertilizationValues?.pprice ??
      (metricValue === Metric.metric
        ? '2'
        : unMaskInputValue(String(haToAc(2)))),
    onChangeCallback: changeFertilizationValues('pprice'),
  });

  const {
    value: pricesK,
    setValue: setPricesK,
    clean: cleanpricesk,
  } = useMaskedInput({
    defaultValue:
      metricValue === Metric.metric
        ? '1,5'
        : unMaskInputValue(String(haToAc(1.5))),
    value:
      fertilizationValues?.kprice ??
      (metricValue === Metric.metric
        ? '1,5'
        : unMaskInputValue(String(haToAc(1.5)))),
    onChangeCallback: changeFertilizationValues('kprice'),
  });

  const {
    value: efficiencyN,
    setValue: setEfficiencyN,
    clean: cleaneffiN,
  } = useMaskedInput({
    defaultValue: '0,1',
    value: fertilizationValues?.efficiencyN ?? '0,1',
    onChangeCallback: changeFertilizationValues('efficiencyN'),
  });

  const {
    value: efficiencyP,
    setValue: setEfficiencyP,
    clean: cleaneffiP,
  } = useMaskedInput({
    defaultValue: '0,1',
    value: fertilizationValues?.efficiencyP ?? '0,1',
    onChangeCallback: changeFertilizationValues('efficiencyP'),
  });

  const {
    value: efficiencyK,
    setValue: setEfficiencyK,
    clean: cleaneffiK,
  } = useMaskedInput({
    defaultValue: '0,1',
    value: fertilizationValues?.efficiencyK ?? '0,1',
    onChangeCallback: changeFertilizationValues('efficiencyK'),
  });

  useEffect(() => {
    if (fertilizationValues) {
      return;
    }

    if (metricValue === Metric.metric) {
      setInputN('180');
      setInputP('50');
      setInputK('70');
      setPricesN('2');
      setPricesP('2');
      setPricesK('1,5');
    } else {
      setInputN(unMaskInputValue(String(haToAc(180))));
      setInputP(unMaskInputValue(String(haToAc(50))));
      setInputK(unMaskInputValue(String(haToAc(70))));
      setPricesN(unMaskInputValue(String(haToAc(2))));
      setPricesP(unMaskInputValue(String(haToAc(2))));
      setPricesK(unMaskInputValue(String(haToAc(1.5))));
    }
  }, [metricValue]);

  const resetData = () => {
    if (metricValue === Metric.metric) {
      setInputN('180');
      setInputP('50');
      setInputK('70');
      setPricesN('2');
      setPricesP('2');
      setPricesK('1,5');
    } else {
      setInputN(unMaskInputValue(String(haToAc(180))));
      setInputP(unMaskInputValue(String(haToAc(50))));
      setInputK(unMaskInputValue(String(haToAc(70))));
      setPricesN(unMaskInputValue(String(haToAc(2))));
      setPricesP(unMaskInputValue(String(haToAc(2))));
      setPricesK(unMaskInputValue(String(haToAc(1.5))));
    }
  };

  return {
    // NPK Inputs
    inputN,
    setInputN,
    cleaninputn,
    inputP,
    setInputP,
    cleaninputp,
    inputK,
    setInputK,
    cleaninputk,
    
    // Prices
    pricesN,
    setPricesN,
    cleanpricesn,
    pricesP,
    setPricesP,
    cleanpricesp,
    pricesK,
    setPricesK,
    cleanpricesk,
    
    // Efficiency
    efficiencyN,
    setEfficiencyN,
    cleaneffiN,
    efficiencyP,
    setEfficiencyP,
    cleaneffiP,
    efficiencyK,
    setEfficiencyK,
    cleaneffiK,
    
    resetData,
  };
}; 